/**
 * Inline "New gate" form. Creates an Access Control location on the event
 * with the same row shape as the Location create page, but asks only for
 * what a gate needs: a name, the vendor it reports under, an optional zone,
 * and the ticket types it accepts. Everything else takes the value every
 * recent gate has (clerks off, 0% fee, "RPOS <event>" descriptor).
 */
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { Input } from "../../../../components/inputs/Input";
import CustomSelect from "../../../../components/customSelect";
import { PrimaryButton, SecondaryButton } from "../../../../components/Button";
import { CREATE_LOCATION } from "../../../locations/mutations";
import { ACCESS_CONTROL_TYPE, SetupData } from "../types";

const client = createApolloClient();
const { getState: getUserState } = userStore;

/** Same rule as the Location create page: alphanumerics and spaces, 22 characters. */
const descriptorFor = (eventName: string) => `RPOS ${eventName}`.replace(/[^a-zA-Z0-9 ]/g, "").slice(0, 22);

/** The vendor most of the event's locations report under, else the first vendor. */
export const defaultGateVendorId = (data: SetupData): number | null => {
  const counts = new Map<number, number>();
  for (const g of data.gates) if (g.vendor_id != null) counts.set(g.vendor_id, (counts.get(g.vendor_id) ?? 0) + 1);
  let best: number | null = null;
  for (const [id, n] of counts) if (best === null || n > (counts.get(best) ?? 0)) best = id;
  if (best !== null && data.vendors.some((v) => v.id === best)) return best;
  return data.vendors[0]?.id ?? null;
};

export const NewGateForm = ({
  data,
  refresh,
  initialTokens = [],
  onCancel,
  onCreated,
}: {
  data: SetupData;
  refresh: () => void;
  /** Ticket base names the new gate accepts from the start. */
  initialTokens?: string[];
  onCancel: () => void;
  onCreated?: () => void;
}) => {
  const event = data.event!;
  const zones = (event.location_zones ?? []).map(String).filter((z) => z.trim());
  const [name, setName] = useState("");
  const [vendorId, setVendorId] = useState<number | null>(() => defaultGateVendorId(data));
  const [zone, setZone] = useState<string | null>(null);
  const [tokens, setTokens] = useState<string[]>(initialTokens);
  const [saving, setSaving] = useState(false);

  const tokenOptions = useMemo(() => {
    const names = new Set<string>(initialTokens);
    for (const t of data.tickets) if (t.is_active && !t.cashless_top_up_item) names.add(t.name.trim());
    return [...names].filter(Boolean).sort().map((n) => ({ label: n, value: n }));
  }, [data.tickets, initialTokens]);

  const create = async () => {
    const cleanName = name.trim();
    if (!cleanName) return toast.error("The gate needs a name.");
    if (vendorId == null) return toast.error("Pick the vendor this gate reports under.");
    setSaving(true);
    try {
      await client.mutate({
        mutation: CREATE_LOCATION,
        variables: {
          input: {
            name: cleanName,
            is_active: true,
            location_type: ACCESS_CONTROL_TYPE,
            event_id: event.id,
            vendor_id: vendorId,
            zone: zone || null,
            redeemable_tokens: tokens,
            payment_processor_id: event.payment_processor_id ?? event.ticketing_payment_processor_id ?? null,
            dynamic_descriptor: descriptorFor(event.name),
            digital_surcharge_percentage: 0,
            users_required: false,
            users_password_required: false,
            last_updated_by: getUserState().user?.id,
          },
        },
      });
      await client.clearStore();
      toast.success(`Gate "${cleanName}" created`);
      onCreated?.();
      refresh();
    } catch (err) {
      toast.error("Failed to create gate: " + err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-dashed border-gray-300 rounded-md p-4 bg-gray-50 flex flex-col gap-3">
      <div>
        <div className="text-sm font-semibold text-gray-900">New gate</div>
        <div className="text-sm text-gray-600">An Access Control location on this event. Tablets at this gate scan the ticket types listed here.</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input label="Gate name" name="gate_name" value={name} onChange={(v) => setName(String(v ?? ""))} required />
        <CustomSelect title="Vendor" name="gate_vendor_id" options={data.vendors.map((v) => ({ label: v.name, value: v.id }))} value={vendorId} onChange={(v) => setVendorId(v == null ? null : Number(v))} />
        {zones.length > 0 && (
          <CustomSelect title="Zone (optional)" name="gate_zone" isClearable options={zones.map((z) => ({ label: z, value: z }))} value={zone} onChange={(v) => setZone(v ? String(v) : null)} />
        )}
        <div className="md:col-span-3">
          <CustomSelect title="Ticket types this gate accepts" name="gate_tokens" multi closeOnSelect={false} isClearable options={tokenOptions} value={tokens} onChange={(v) => setTokens(Array.isArray(v) ? v.map(String) : [])} />
          <div className="text-xs text-gray-500 mt-1">A ticket with modifiers is accepted in every combination when its base name is listed. Narrow it per ticket in that ticket's Gates step.</div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        <PrimaryButton isDisabled={saving} onClick={create}>{saving ? "Creating…" : "Create gate"}</PrimaryButton>
      </div>
    </div>
  );
};
